import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearIterationReviews,
  loadReviews,
  saveReview as persistSaveReview,
  setNotes as persistSetNotes,
} from '../../components/billingReconciliation/persistence.js';
import { isBillingReconciliationMockMode } from '../api/billingReconciliationApi.js';
import {
  useCreateBillingReconciliationNote,
  useDeleteBillingReconciliationNote,
  useUpdateBillingReconciliationNote,
  useUpdateBillingReconciliationVarianceReview,
} from '../api/useBillingReconciliationHooks.js';

const errorMessage = (error) => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Review save failed.'
);

const toVariancePatch = (patch = {}) => {
  const next = {};
  if (Object.prototype.hasOwnProperty.call(patch, 'category')) next.reviewDriver = patch.category;
  if (Object.prototype.hasOwnProperty.call(patch, 'remediation')) next.remediation = patch.remediation;
  if (Object.prototype.hasOwnProperty.call(patch, 'reviewed')) next.reviewed = patch.reviewed;
  if (Object.prototype.hasOwnProperty.call(patch, 'resolveActionsOnComplete')) {
    next.resolveActionsOnComplete = patch.resolveActionsOnComplete;
  }
  return next;
};

const entityRef = (rowOrBac) => (typeof rowOrBac === 'object' && rowOrBac
  ? {
      bac: rowOrBac.bac,
      varianceId: rowOrBac.id || rowOrBac.varianceId,
      runId: rowOrBac.runId || rowOrBac.run_id || rowOrBac.roundRunId || rowOrBac.round_run_id || null,
      updatedAt: rowOrBac.updatedAt || rowOrBac.updated_at,
    }
  : { bac: rowOrBac, varianceId: null, runId: null, updatedAt: null });

const isServerNoteId = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(id || ''))
  || String(id || '').startsWith('note-');

const noteDraftFlag = (note) => note?.status === 'draft' || note?.isDraft === true;
const noteResponseHasDraftFlag = (note = {}) => (
  Object.prototype.hasOwnProperty.call(note, 'isDraft') ||
  Object.prototype.hasOwnProperty.call(note, 'is_draft')
);
const noteResponseDraftFlag = (note = {}, fallback = false) => (
  noteResponseHasDraftFlag(note) ? Boolean(note.isDraft ?? note.is_draft) : fallback
);

const reviewFromServer = (current = {}, saved = {}) => ({
  ...current,
  ...(Object.prototype.hasOwnProperty.call(saved, 'reviewDriver') || Object.prototype.hasOwnProperty.call(saved, 'review_driver')
    ? { category: saved.reviewDriver ?? saved.review_driver ?? null }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(saved, 'remediation') ? { remediation: saved.remediation ?? null } : {}),
  ...(Object.prototype.hasOwnProperty.call(saved, 'reviewed') ? { reviewed: Boolean(saved.reviewed) } : {}),
  updatedAt: saved.updatedAt || saved.updated_at || current.updatedAt || null,
});

const rollbackFailedReviewPatch = (currentReview = {}, baseReview = {}, failedPatch = {}) => {
  const rollback = { ...currentReview };
  Object.entries(failedPatch).forEach(([key, value]) => {
    if (key === 'resolveActionsOnComplete') return;
    if (!Object.is(rollback[key], value)) return;
    if (Object.prototype.hasOwnProperty.call(baseReview, key)) rollback[key] = baseReview[key];
    else delete rollback[key];
  });
  return rollback;
};

// Holds the review map ({ [bac]: reviewPayload }) for the currently-active
// iteration, plus the scoped save helpers the drawer uses to write through.
// Callers rebind the map whenever `activeIterationId` changes so that
// switching rounds shows that round's signed-off state.
export const useReviewsState = (activeIterationId) => {
  const mockMode = isBillingReconciliationMockMode();
  const updateReview = useUpdateBillingReconciliationVarianceReview();
  const createNote = useCreateBillingReconciliationNote();
  const updateNote = useUpdateBillingReconciliationNote();
  const deleteNote = useDeleteBillingReconciliationNote();
  const [reviews, setReviews] = useState(() => (mockMode ? loadReviews(activeIterationId) : {}));
  const [saveError, setSaveError] = useState(null);
  const reviewsRef = useRef(reviews);
  const reviewSaveQueuesRef = useRef(new Map());
  const latestReviewUpdatedAtRef = useRef(new Map());
  const latestReviewSaveErrorRef = useRef(new Map());
  const pendingLocalNoteIdsRef = useRef(new Set());
  const localToServerNoteIdRef = useRef(new Map());

  const setReviewsSynced = useCallback((updater) => {
    setReviews((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      reviewsRef.current = next;
      return next;
    });
  }, []);

  const replaceLocalNote = useCallback((bac, localId, serverNote, options = {}) => {
    const createdAt = serverNote.createdAt || serverNote.created_at;
    const updatedAt = serverNote.updatedAt || serverNote.updated_at;
    setReviewsSynced((prev) => {
      const current = prev[bac] || {};
      const notes = Array.isArray(current.notes) ? current.notes : [];
      return {
        ...prev,
        [bac]: {
          ...current,
          notes: notes.map((note) => (
            note.id === localId
              ? {
                  ...note,
                  id: serverNote.id,
                  ts: createdAt || note.ts,
                  updatedAt: updatedAt || note.updatedAt,
                  status: (
                    typeof options.isDraft === 'boolean'
                      ? options.isDraft
                      : noteResponseDraftFlag(serverNote, noteDraftFlag(note))
                  ) ? 'draft' : 'final',
                }
              : note
          )),
        },
      };
    });
  }, [setReviewsSynced]);

  const replaceServerNote = useCallback((bac, noteId, serverNote = {}) => {
    const updatedAt = serverNote.updatedAt || serverNote.updated_at;
    setReviewsSynced((prev) => {
      const current = prev[bac] || {};
      const notes = Array.isArray(current.notes) ? current.notes : [];
      return {
        ...prev,
        [bac]: {
          ...current,
          notes: notes.map((note) => (
            note.id === noteId
              ? {
                  ...note,
                  text: serverNote.text ?? note.text,
                  updatedAt: updatedAt || note.updatedAt,
                  status: noteResponseDraftFlag(serverNote, noteDraftFlag(note)) ? 'draft' : 'final',
                }
              : note
          )),
        },
      };
    });
  }, [setReviewsSynced]);

  useEffect(() => {
    const next = mockMode ? loadReviews(activeIterationId) : {};
    reviewsRef.current = next;
    reviewSaveQueuesRef.current.clear();
    latestReviewUpdatedAtRef.current.clear();
    latestReviewSaveErrorRef.current.clear();
    pendingLocalNoteIdsRef.current.clear();
    localToServerNoteIdRef.current.clear();
    setSaveError(null);
    setReviews(next);
  }, [activeIterationId, mockMode]);

  const handleReviewChange = useCallback((bac, next) => {
    setReviewsSynced((prev) => ({ ...prev, [bac]: next }));
  }, [setReviewsSynced]);

  const resetReviews = useCallback(() => {
    if (!mockMode) {
      setReviewsSynced({});
      return;
    }
    clearIterationReviews(activeIterationId);
    setReviewsSynced({});
  }, [activeIterationId, mockMode, setReviewsSynced]);

  const resetReviewFlags = useCallback(({ bacs = [], all = false } = {}) => {
    const bacSet = new Set((Array.isArray(bacs) ? bacs : [bacs]).map((bac) => String(bac || '').trim()).filter(Boolean));
    setReviewsSynced((prev) => Object.fromEntries(Object.entries(prev).map(([bac, review]) => {
      if (!all && !bacSet.has(String(bac))) return [bac, review];
      return [bac, { ...(review || {}), reviewed: false }];
    })));
  }, [setReviewsSynced]);

  const clearSaveError = useCallback(() => {
    setSaveError(null);
    latestReviewSaveErrorRef.current.clear();
    updateReview.reset?.();
    createNote.reset?.();
    updateNote.reset?.();
    deleteNote.reset?.();
  }, [createNote, deleteNote, updateNote, updateReview]);

  // Drawer writes go through here — scopes saves to the current iteration.
  const saveReviewScoped = useCallback(
    (rowOrBac, patch) => {
      const target = entityRef(rowOrBac);
      const targetRunId = target.runId || activeIterationId;
      const saveKey = target.varianceId ? `${targetRunId}:${target.varianceId}` : `${targetRunId}:${target.bac}`;
      const next = mockMode
        ? persistSaveReview(targetRunId, target.bac, patch)
        : { ...(reviewsRef.current[target.bac] || { notes: [] }), ...patch };
      const previousReview = reviewsRef.current[target.bac] || { notes: [] };
      delete next.resolveActionsOnComplete;
      if (!mockMode) {
        clearSaveError();
        latestReviewSaveErrorRef.current.delete(saveKey);
        const prior = reviewSaveQueuesRef.current.get(saveKey) || Promise.resolve();
        const queued = prior
          .catch(() => {})
          .then(async () => {
            const current = reviewsRef.current[target.bac] || {};
            const saved = await updateReview.mutateAsync({
              runId: targetRunId,
              varianceId: target.varianceId,
              bac: target.bac,
              ifUpdatedAt: current.updatedAt || latestReviewUpdatedAtRef.current.get(saveKey) || target.updatedAt,
              patch: toVariancePatch(patch),
            });
            const savedUpdatedAt = saved?.updatedAt || saved?.updated_at || null;
            if (savedUpdatedAt) latestReviewUpdatedAtRef.current.set(saveKey, savedUpdatedAt);
            latestReviewSaveErrorRef.current.delete(saveKey);
            setReviewsSynced((prev) => ({
              ...prev,
              [target.bac]: reviewFromServer(prev[target.bac] || next, saved),
            }));
          })
          .catch((error) => {
            setSaveError(error);
            latestReviewSaveErrorRef.current.set(saveKey, errorMessage(error));
            setReviewsSynced((prev) => {
              const currentReview = prev[target.bac] || reviewsRef.current[target.bac] || { notes: [] };
              const rollbackReview = rollbackFailedReviewPatch(currentReview, previousReview, patch);
              return {
                ...prev,
                [target.bac]: {
                  ...rollbackReview,
                  saveError: errorMessage(error),
                },
              };
            });
          })
          .finally(() => {
            if (reviewSaveQueuesRef.current.get(saveKey) === queued) reviewSaveQueuesRef.current.delete(saveKey);
          });
        reviewSaveQueuesRef.current.set(saveKey, queued);
      }
      setReviewsSynced((prev) => {
        if (mockMode) return { ...prev, [target.bac]: next };
        return {
          ...prev,
          [target.bac]: {
            ...(prev[target.bac] || reviewsRef.current[target.bac] || { notes: [] }),
            ...patch,
          },
        };
      });
      return next;
    },
    [activeIterationId, clearSaveError, mockMode, setReviewsSynced, updateReview],
  );
  const saveReviewScopedAsync = useCallback(
    async (rowOrBac, patch) => {
      const target = entityRef(rowOrBac);
      const targetRunId = target.runId || activeIterationId;
      const next = saveReviewScoped(rowOrBac, patch);
      if (mockMode) return next;
      const saveKey = target.varianceId ? `${targetRunId}:${target.varianceId}` : `${targetRunId}:${target.bac}`;
      const queued = reviewSaveQueuesRef.current.get(saveKey);
      if (queued) await queued;
      const queuedError = latestReviewSaveErrorRef.current.get(saveKey);
      if (queuedError) throw new Error(queuedError);
      const current = reviewsRef.current[target.bac] || next;
      if (current.saveError) {
        throw new Error(current.saveError);
      }
      return current;
    },
    [activeIterationId, mockMode, saveReviewScoped],
  );
  const setNotesScoped = useCallback(
    (rowOrBac, notes) => {
      const target = entityRef(rowOrBac);
      const targetRunId = target.runId || activeIterationId;
      const previousNotes = Array.isArray(reviewsRef.current[target.bac]?.notes)
        ? reviewsRef.current[target.bac].notes
        : [];
      const next = mockMode
        ? persistSetNotes(targetRunId, target.bac, notes)
        : { ...(reviewsRef.current[target.bac] || { reviewed: false, category: null, remediation: null }), notes };
      if (!mockMode && target.varianceId) {
        const nextById = new Map(notes.filter((note) => note?.id).map((note) => [note.id, note]));
        previousNotes
          .filter((note) => note?.id && !nextById.has(note.id))
          .forEach((note) => {
            const serverId = localToServerNoteIdRef.current.get(note.id) || (isServerNoteId(note.id) ? note.id : null);
            if (serverId) {
              deleteNote.mutate({
                id: serverId,
                runId: targetRunId,
                varianceId: target.varianceId,
                ifUpdatedAt: note.updatedAt || note.updated_at,
              });
            }
          });
        notes.forEach((note) => {
          const isDraft = noteDraftFlag(note);
          const serverId = localToServerNoteIdRef.current.get(note?.id) || (isServerNoteId(note?.id) ? note.id : null);
          if (!serverId) {
            if (String(note?.text || '').trim()) {
              if (pendingLocalNoteIdsRef.current.has(note.id)) return;
              pendingLocalNoteIdsRef.current.add(note.id);
              createNote.mutate({
                runId: targetRunId,
                varianceId: target.varianceId,
                text: note.text,
                isDraft,
              }, {
                onSuccess: (created) => {
                  pendingLocalNoteIdsRef.current.delete(note.id);
                  localToServerNoteIdRef.current.set(note.id, created.id);
                  const currentNote = reviewsRef.current[target.bac]?.notes?.find((candidate) => (
                    candidate.id === note.id || candidate.id === created.id
                  ));
                  if (!currentNote) {
                    deleteNote.mutate({
                      id: created.id,
                      runId: targetRunId,
                      varianceId: target.varianceId,
                      ifUpdatedAt: created.updatedAt || created.updated_at,
                    });
                    return;
                  }
                  const currentIsDraft = noteDraftFlag(currentNote);
                  replaceLocalNote(target.bac, note.id, created, { isDraft: currentIsDraft });
                  if (currentNote.text !== created.text || currentIsDraft !== noteResponseDraftFlag(created, false)) {
                    updateNote.mutate({
                      id: created.id,
                      runId: targetRunId,
                      varianceId: target.varianceId,
                      ifUpdatedAt: created.updatedAt || created.updated_at,
                      patch: { text: currentNote.text, isDraft: currentIsDraft },
                    }, {
                      onSuccess: (updated) => replaceServerNote(target.bac, created.id, updated),
                    });
                  }
                },
                onError: () => {
                  pendingLocalNoteIdsRef.current.delete(note.id);
                },
              });
            }
            return;
          }
          const previous = previousNotes.find((candidate) => candidate.id === note.id);
          if (!previous || previous.text !== note.text || noteDraftFlag(previous) !== isDraft) {
            updateNote.mutate({
              id: serverId,
              runId: targetRunId,
              varianceId: target.varianceId,
              ifUpdatedAt: previous?.updatedAt || previous?.updated_at || note.updatedAt || note.updated_at,
              patch: { text: note.text, isDraft },
            }, {
              onSuccess: (updated) => replaceServerNote(target.bac, note.id, updated),
            });
          }
        });
      }
      setReviewsSynced((prev) => ({ ...prev, [target.bac]: next }));
      return next;
    },
    [activeIterationId, createNote, deleteNote, mockMode, replaceLocalNote, replaceServerNote, setReviewsSynced, updateNote],
  );

  return {
    reviews,
    handleReviewChange,
    resetReviews,
    resetReviewFlags,
    saveReviewScoped,
    saveReviewScopedAsync,
    setNotesScoped,
    isSaving: updateReview.isPending || createNote.isPending || updateNote.isPending || deleteNote.isPending,
    error: saveError || updateReview.error || createNote.error || updateNote.error || deleteNote.error,
    clearError: clearSaveError,
  };
};
